import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Button, TextField, MenuItem, Alert, CircularProgress,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { listMyAthletes } from '../../services/athleteService';
import { previewTeamJoinCode, requestTeamRosterJoin } from '../../services/rosterService';

/**
 * Guardian-facing entry point for the team-generated join code (012) —
 * the primary V1 onboarding path, alongside the athlete-specific code a
 * guardian generates themselves (see TeamMembershipSection, hidden from
 * normal V1 UI). A coach shares a code with a whole team's families; a
 * guardian enters it here, sees the team's identity BEFORE committing to
 * anything, picks ONE of their own actively-guarded athletes, and
 * submits a request — which only ever creates a PENDING request, never
 * roster membership directly. Only a Team Admin/Head Coach approval (on
 * the team's Roster Requests page) creates the actual active
 * team_athletes row. There is no team search here — the code is the
 * only way in.
 *
 * Reachable two ways (017), rendered by this same component throughout —
 * only the presence of the :code route param differs:
 *   - /athletes/join-team — manual entry. A guardian types a code a coach
 *     handed them directly. The lookup card always stays visible here.
 *   - /join/:code — a shareable direct link. The code arrives pre-filled
 *     and the lookup runs automatically; the manual lookup card is never
 *     shown at all in this mode (the guardian already arrived through a
 *     direct invitation and shouldn't be asked to re-enter/look up
 *     anything) — once resolved, both modes converge on the identical
 *     athlete-selection / create-athlete states below.
 */
function JoinTeamInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { code: codeParam } = useParams(); // present only on /join/:code
  const isDirectInvite = Boolean(codeParam);
  const [code, setCode] = useState(codeParam ? codeParam.toUpperCase() : '');
  const [team, setTeam] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [athletes, setAthletes] = useState([]);
  const [athletesLoading, setAthletesLoading] = useState(true);
  const [athletesError, setAthletesError] = useState(null);
  const [selectedAthleteId, setSelectedAthleteId] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(null); // the resulting request, once submitted

  // A failed fetch must never read as "this guardian has zero athletes" —
  // that would silently show the Create Athlete state to someone who
  // actually has athletes but hit a transient network/RLS hiccup. Tracked
  // as its own error state instead of swallowed, with a retry action.
  const loadAthletes = useCallback(() => {
    setAthletesLoading(true);
    setAthletesError(null);
    return listMyAthletes()
      .then(setAthletes)
      .catch(e => setAthletesError(e.message || 'Could not load your athletes.'))
      .finally(() => setAthletesLoading(false));
  }, []);

  useEffect(() => { loadAthletes(); }, [loadAthletes]);

  const lookUp = async (rawCode) => {
    setPreviewError(null);
    setTeam(null);
    setSubmitted(null);
    if (!rawCode.trim()) { setPreviewError('Enter the team access code a coach shared with you.'); return; }
    setPreviewLoading(true);
    try {
      const t = await previewTeamJoinCode(rawCode.trim());
      setTeam(t);
    } catch (e2) {
      setPreviewError(e2.message || 'Could not look up that code.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Auto-run the lookup exactly once when arriving via /join/:code — the
  // "Look Up Team" step a manual-entry visitor clicks themselves. Guarded
  // by a ref (not a [] dependency on codeParam alone) so it fires once
  // per mount even if this component re-renders for unrelated reasons
  // (e.g. the athletes fetch above resolving), never re-triggers on every
  // keystroke if the guardian then edits the pre-filled code by hand.
  const autoLookedUp = useRef(false);
  useEffect(() => {
    if (codeParam && !autoLookedUp.current) {
      autoLookedUp.current = true;
      lookUp(codeParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeParam]);

  const handleLookUp = async (e) => {
    e.preventDefault();
    await lookUp(code);
  };

  const handleRequest = async () => {
    setSubmitError(null);
    if (!selectedAthleteId) { setSubmitError('Choose which athlete this request is for.'); return; }
    setSubmitting(true);
    try {
      const request = await requestTeamRosterJoin(code.trim(), selectedAthleteId);
      setSubmitted(request);
    } catch (e2) {
      setSubmitError(e2.message || 'Could not submit that request.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedAthlete = athletes.find(a => a.id === selectedAthleteId);
  const returnTo = `${location.pathname}`; // works for both /join/:code and /athletes/join-team

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
        {isDirectInvite ? "You've Been Invited" : 'Enter Team Access Code'}
      </Typography>
      <Typography sx={{ color: '#9eb8d0', mb: 3 }}>
        {isDirectInvite
          ? "Requesting to join doesn't add anyone to the roster by itself — the team still has to approve it."
          : "A coach shares this code with families to register for a team. Entering it doesn't add anyone to a roster by itself — it lets you request to join, which the team still has to approve."}
      </Typography>

      {/* Manual lookup card — /athletes/join-team only. A direct-invite
          guardian already arrived through the link itself and is never
          asked to re-enter or look up a code, successful or not. */}
      {!isDirectInvite && (
        <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
          <CardContent>
            <Box component="form" onSubmit={handleLookUp} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <TextField
                label="Team Access Code" value={code} onChange={e => setCode(e.target.value.toUpperCase())}
                placeholder="ABCD2345" inputProps={{ maxLength: 8, style: { fontFamily: 'monospace', letterSpacing: '0.12em' } }}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <Button type="submit" variant="contained" color="primary" disabled={previewLoading} sx={{ textTransform: 'none', fontWeight: 700, height: 56 }}>
                {previewLoading ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Look Up Team'}
              </Button>
            </Box>
            {previewError && <Alert severity="error" sx={{ mt: 2 }}>{previewError}</Alert>}
          </CardContent>
        </Card>
      )}

      {isDirectInvite && previewLoading && !team && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress size={24} sx={{ color: '#2ea84b' }} />
        </Box>
      )}

      {isDirectInvite && previewError && (
        <Alert severity="error" sx={{ mt: 1 }}>{previewError}</Alert>
      )}

      {team && !submitted && (
        <Card sx={{ mt: 3, backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #2ea84b' }}>
          <CardContent>
            <Typography sx={{ fontWeight: 900, fontSize: '1.3rem' }}>
              Register an athlete for the {team.team_name}
            </Typography>
            <Typography sx={{ color: '#9eb8d0', mt: 0.25 }}>{team.grade_age_group} · {team.season}</Typography>
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.9rem' }}>{team.city}, {team.state}</Typography>

            {athletesLoading ? (
              <Box sx={{ mt: 3 }}><CircularProgress size={20} sx={{ color: '#2ea84b' }} /></Box>
            ) : athletesError ? (
              <Box sx={{ mt: 3 }}>
                <Alert severity="error" sx={{ mb: 1.5 }}>{athletesError}</Alert>
                <Button variant="outlined" color="primary" onClick={loadAthletes} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Retry
                </Button>
              </Box>
            ) : (
              <Box sx={{ mt: 3 }}>
                <Typography sx={{ color: '#9eb8d0', mb: 2 }}>
                  The {team.team_name} have invited you to request roster access.
                </Typography>

                {/* Choosing an existing athlete and creating a new one are
                    both always available once athletes have loaded — never
                    only one or the other. Creating an athlete NEVER submits
                    the request by itself; Request to Join stays a fully
                    separate, explicit action below. */}
                {athletes.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Choose an existing athlete</Typography>
                    <TextField
                      select fullWidth value={selectedAthleteId} onChange={e => setSelectedAthleteId(e.target.value)}
                      sx={{ maxWidth: 320 }}
                    >
                      <MenuItem value="">Select an athlete</MenuItem>
                      {athletes.map(a => (
                        <MenuItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                )}

                {athletes.length > 0 && (
                  <Typography sx={{ color: '#4a6080', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.05em', mb: 2 }}>
                    OR
                  </Typography>
                )}

                <Button
                  variant={athletes.length > 0 ? 'outlined' : 'contained'} color="primary"
                  onClick={() => navigate(`/athletes/new?returnTo=${encodeURIComponent(returnTo)}`)}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  + Create Athlete
                </Button>
                <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem', mt: 1.5, mb: athletes.length > 0 ? 2.5 : 0 }}>
                  You'll return here automatically when you're finished.
                </Typography>

                {/* No Request to Join at all with zero athletes — there is
                    nothing yet to request with; it appears once at least
                    one athlete exists, disabled until one is selected. */}
                {athletes.length > 0 && (
                  <Button
                    variant="contained" color="primary" disabled={submitting || !selectedAthleteId} onClick={handleRequest}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    {submitting ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Request to Join'}
                  </Button>
                )}
              </Box>
            )}

            {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
          </CardContent>
        </Card>
      )}

      {submitted && (
        <Alert severity={submitted.status === 'approved' ? 'success' : 'info'} sx={{ mt: 3 }}>
          {submitted.status === 'approved'
            ? `${selectedAthlete?.first_name} is now on ${team.team_name}'s roster.`
            : `Request sent — ${selectedAthlete?.first_name}'s membership on ${team.team_name} is pending the team's approval. You can check its status from ${selectedAthlete?.first_name}'s profile.`}
          <Box sx={{ mt: 1.5 }}>
            <Button size="small" onClick={() => navigate(`/athletes/${selectedAthleteId}`)} sx={{ textTransform: 'none', fontWeight: 700 }}>
              View {selectedAthlete?.first_name}'s Profile →
            </Button>
          </Box>
        </Alert>
      )}
    </Container>
  );
}

export default function JoinTeamPage() {
  const { code } = useParams();
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth
        message={code
          ? "You've been invited to join a team on Football for Nerds. Sign in or create an account to continue — you'll pick which of your athletes this invite is for before anything is submitted."
          : undefined}
        offerCreateAccount={Boolean(code)}
      >
        <JoinTeamInner />
      </RequireAuth>
    </Box>
  );
}
